import { queryClient } from "@api/queryClient";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { type Subject, subjectsClient } from "@subjects/subjectsClient";
import { useQuery } from "@tanstack/react-query";

export const subjectsKeys = {
  search: (q: string) => ["subject-search", q] as const,
  detail: (id: string) => ["subject", id] as const,
};

/**
 * The subject the URL is currently pointing at (`subject=<uuid>`), fetched by
 * id so the selection survives a refresh or a shared deep link. Non-suspense
 * and never throws to an error boundary - the URL param is the source of
 * truth, so a stale/missing id shouldn't crash the workspace. A fetch that
 * settles with no record just resolves to the clinic grid (a subject is a full
 * navigation with nothing underneath it), rather than rendering anything.
 */
export const useSubject = (id: string | null) =>
  useQuery({
    queryKey: subjectsKeys.detail(id ?? ""),
    queryFn: () => subjectsClient.get(id!),
    enabled: id !== null,
    staleTime: 60_000,
    // A 404 is definitive (the record was deleted, or a foreign uuid landed on
    // a deep link) - retrying would just hold the workspace blank for the
    // backoff window before the param resolves to the clinic grid. Everything
    // else keeps the client's default retry.
    retry: (failureCount, error) =>
      error instanceof ClinicAuthError && error.status === 404
        ? false
        : failureCount < 2,
  });

/**
 * Navigation entry points that already hold the full record (the search
 * modal, calendar) call this before navigating so the
 * cache is warm the instant the URL updates - opening a subject never flashes
 * a fetch. The query above only hits the network when there's nothing to
 * seed from: a page refresh or a deep link.
 */
export const seedSubject = (subject: Subject): void => {
  queryClient.setQueryData(subjectsKeys.detail(subject.id), subject);
};

export const useSubjectSearch = (q: string) =>
  useQuery({
    queryKey: subjectsKeys.search(q),
    queryFn: () => subjectsClient.search(q),
    enabled: q.length > 0,
    staleTime: 30_000,
  });
